// 묶음 미리 받기. 묶음(bundles.ts)에 든 파일을 낮은 동시 수로 받아 HTTP 캐시·서비스 워커 캐시에 채운다.
// 순수에 가깝다: 네트워크·타이머는 주입받는다(시험은 가짜로, 실제는 regionData.ts 가 fetch·setTimeout 을 넣는다).
import { BUNDLE_IDS, bundleOf, type BundleId } from './bundles';
import type { AssetEntry } from './assetMap';

export type BundleState = 'idle' | 'queued' | 'running' | 'done' | 'failed';

export interface BundleStatus {
  state: BundleState;
  files: number;
  filesDone: number;
  bytesTotal: number;
  bytesDone: number;
  /** 0..100 정수(받을 것이 없으면 100) */
  pct: number;
}

export interface PrefetchEnv {
  /** 한 파일을 끝까지 받는다. 성공하면 true */
  fetchOne(url: string): Promise<boolean>;
  /** 이미 캐시에 있으면 true(받지 않고 완료로 센다). 없으면 생략 */
  has?(url: string): Promise<boolean>;
  wait(ms: number): Promise<void>;
  now(): number;
}

export interface PrefetchOpts {
  /** 한가할 때 동시 요청 수 */
  idleConcurrency?: number;
  /** 입장이 임박했을 때 동시 요청 수 */
  urgentConcurrency?: number;
  /** 파일마다 시도 횟수 */
  attempts?: number;
  /** 실패한 묶음을 다시 시도하기까지(ms) */
  retryAfterMs?: number;
}

interface Job { id: string; entries: AssetEntry[]; urgent: boolean; filesDone: number; bytesDone: number; failed: boolean; failedAt: number; queued: boolean; started: boolean; finished: boolean; next: number }

export class BundlePrefetcher {
  private readonly jobs = new Map<string, Job>();
  private active = 0;
  private paused = false;
  /** 급한 묶음까지 멈춘다(새 버전 적용 중: 서비스 워커가 새 요청 없이 바로 넘어가게) */
  private hard = false;
  private readonly idleC: number; private readonly urgentC: number; private readonly attempts: number; private readonly retryAfter: number;
  private listeners: Array<() => void> = [];

  constructor(private readonly entries: readonly AssetEntry[], private readonly urlOf: (rel: string) => string, private readonly env: PrefetchEnv, opts: PrefetchOpts = {}) {
    this.idleC = opts.idleConcurrency ?? 2;
    this.urgentC = opts.urgentConcurrency ?? 6;
    this.attempts = opts.attempts ?? 3;
    this.retryAfter = opts.retryAfterMs ?? 30000;
    for (const id of BUNDLE_IDS) this.jobs.set(id, this.makeJob(id, this.entries.filter((e) => bundleOf(e.rel) === id)));
  }

  private makeJob(id: string, entries: AssetEntry[]): Job {
    return { id, entries, urgent: false, filesDone: 0, bytesDone: 0, failed: false, failedAt: 0, queued: false, started: false, finished: entries.length === 0, next: 0 };
  }

  /** 임의 파일 목록(패치 대상)을 'patch:<이름>' 묶음으로 올린다 */
  addCustom(id: string, entries: AssetEntry[]): void { this.jobs.set(id, this.makeJob(id, entries)); }

  onChange(fn: () => void): () => void { this.listeners.push(fn); return () => { this.listeners = this.listeners.filter((f) => f !== fn); }; }
  private emit(): void { for (const f of this.listeners) f(); }

  status(id: string): BundleStatus {
    const j = this.jobs.get(id);
    if (!j) return { state: 'idle', files: 0, filesDone: 0, bytesTotal: 0, bytesDone: 0, pct: 100 };
    const bytesTotal = j.entries.reduce((s, e) => s + e.bytes, 0);
    const state: BundleState = j.finished ? 'done' : j.failed ? 'failed' : j.started ? 'running' : j.queued ? 'queued' : 'idle';
    const pct = bytesTotal <= 0 ? 100 : Math.min(100, Math.floor((j.bytesDone / bytesTotal) * 100));
    return { state, files: j.entries.length, filesDone: j.filesDone, bytesTotal, bytesDone: j.bytesDone, pct: j.finished ? 100 : Math.min(99, pct) };
  }

  isDone(id: string): boolean { return this.jobs.get(id)?.finished === true; }

  /** 묶음을 대기열에 올린다. urgent 면 앞으로 당기고 동시 수를 늘린다 */
  request(id: string, urgent = false): void {
    const j = this.jobs.get(id);
    if (!j || j.finished) return;
    if (j.failed && !urgent && this.env.now() - j.failedAt < this.retryAfter) return;
    if (j.failed) { j.failed = false; j.next = 0; j.filesDone = 0; j.bytesDone = 0; j.started = false; }
    j.queued = true;
    if (urgent) j.urgent = true;
    this.pump();
  }

  /** 한가할 때 받는 대기열을 멈추거나 다시 연다(급한 묶음은 계속한다) */
  setPaused(p: boolean, hard = false): void { this.paused = p; this.hard = p && hard; if (!p) this.pump(); }

  private concurrency(): number { for (const j of this.jobs.values()) if (j.urgent && !j.finished && j.queued) return this.urgentC; return this.idleC; }

  /** 다음에 받을 묶음: 급한 것 먼저, 그다음 올린 순서(BUNDLE_IDS 순·커스텀은 뒤) */
  private pick(): Job | null {
    let best: Job | null = null;
    for (const j of this.jobs.values()) {
      if (!j.queued || j.finished || j.failed) continue;
      if (this.paused && (!j.urgent || this.hard)) continue;
      if (j.next >= j.entries.length) continue;
      if (!best || (j.urgent && !best.urgent)) best = j;
    }
    return best;
  }

  private pump(): void {
    while (this.active < this.concurrency()) {
      const j = this.pick();
      if (!j) break;
      const e = j.entries[j.next++];
      j.started = true;
      this.active++;
      void this.runOne(j, e).finally(() => { this.active--; this.settle(j); this.pump(); });
    }
  }

  private async runOne(j: Job, e: AssetEntry): Promise<void> {
    const url = this.urlOf(e.rel);
    try {
      if (this.env.has && (await this.env.has(url))) { j.filesDone++; j.bytesDone += e.bytes; this.emit(); return; }
    } catch { /* 캐시 조회 실패는 받기로 이어간다 */ }
    for (let a = 1; a <= this.attempts; a++) {
      let ok = false;
      try { ok = await this.env.fetchOne(url); } catch { ok = false; }
      if (ok) { j.filesDone++; j.bytesDone += e.bytes; this.emit(); return; }
      if (a < this.attempts) await this.env.wait(1000 * a * a);
    }
    j.failed = true; j.failedAt = this.env.now();
    this.emit();
  }

  private settle(j: Job): void {
    if (j.failed) { j.queued = false; j.urgent = false; this.emit(); return; }
    if (j.next >= j.entries.length && j.filesDone >= j.entries.length) { j.finished = true; j.queued = false; j.urgent = false; this.emit(); }
  }
}
